// TypeScript types for the Roadmap Builder
// Ported from the prototype's @/lib/types (adapted for MongoDB string IDs)

export interface WMBT {
  category: string;                    // Legacy single-category field (backward compat)
  categories?: string[];               // Multi-tag categories (preferred — takes precedence over `category`)
  label: string;
  value: string | null;
  isAssumption: boolean;
  assumptionExplanation: string | null;
  source: string | null;
  userValidated: boolean;
  categoryObjective?: string | null;
}

/** Return the effective categories for a WMBT item (prefers `categories[]`, falls back to `category`). */
export function getWmbtCategories(item: WMBT): string[] {
  if (item.categories && item.categories.length > 0) return item.categories;
  return item.category ? [item.category] : [];
}

/** Return the primary (first) category for grouping purposes. */
export function getWmbtPrimaryCategory(item: WMBT): string {
  if (item.categories && item.categories.length > 0) return item.categories[0];
  return item.category || "";
}

/**
 * Normalize a raw WMBT-like object from the database into the current schema.
 *
 * Multiple legacy shapes have shipped into MongoDB over time:
 * - {id, objective, isAssumption, source}                    — earliest legacy
 * - {text, isAssumption, source}                             — emitted by
 *     research-integration AI when the prompt doesn't spec wmbt schema
 *     (see vo_prompts.py:get_research_integration_prompt)
 * - {category, label, value, isAssumption, ...}              — current schema
 *
 * This maps every known field-name to the canonical `label` slot and filters
 * out items that have no meaningful content.
 */
export function normalizeWmbtItems(raw: any[]): WMBT[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw
    .map((item: any) => ({
      category: item.category || "",
      categories: item.categories || undefined,
      // `description` is the research-integration (Perplexity) alias — the Vo
      // prompt forbids it (vo_prompts.py) but real data ships with it, and
      // without this fallback those objectives get label="" and are dropped.
      label: item.label || item.objective || item.text || item.description || "",
      value: item.value ?? null,
      isAssumption: item.isAssumption ?? false,
      assumptionExplanation: item.assumptionExplanation ?? null,
      source: item.source ?? null,
      userValidated: item.userValidated ?? false,
      categoryObjective: item.categoryObjective ?? null,
    }))
    .filter((item) => item.label.trim() || item.categoryObjective?.trim());
}

export interface BuildingBlock {
  status: "empty" | "partial" | "complete";
  draft: string;
  gaps: string[];
  sources: string[];
  // Solo commit state (mirrors team's per-block lock). "blank" is derived from
  // empty content, so it isn't a stored value; optional/defaulted to "draft" for
  // pre-migration data. Replaces the legacy `contributedToTeam` flag.
  commitState?: "draft" | "committed";
}

export interface PeakAmbition extends BuildingBlock {
  headline: string;
  pressRelease: string;
  date: string;
  businessOutcome: string;
  worldImpact: string;
  confirmed: boolean;
}

export interface ObjectivesBlock extends BuildingBlock {
  wmbt: WMBT[];
  customCategories?: string[];
  categoryCatalog?: string[];          // AI-generated + user-edited category labels used in this block
}

export interface AllBuildingBlocks {
  vision: BuildingBlock;
  mission: BuildingBlock;
  peakAmbition: PeakAmbition;
  peakAmbitionObjectives: ObjectivesBlock;
  threeYearObjectives: ObjectivesBlock;
  oneYearObjectives: ObjectivesBlock;
  quarterlyObjectives: ObjectivesBlock;
}

// ── Per-period building block storage ────────────────────────────────────────

export interface PeriodBlockObjectives {
  wmbt: WMBT[];
  customCategories?: string[];
  categoryCatalog?: string[];
  updated_at?: string;
}

export interface PeriodBlockProse {
  draft: string;
  updated_at?: string;
}

export interface PeriodBlockPeakAmbition {
  headline: string;
  pressRelease: string;
  date: string;
  businessOutcome: string;
  worldImpact: string;
  updated_at?: string;
}

export type PeriodBlock = PeriodBlockObjectives | PeriodBlockProse | PeriodBlockPeakAmbition;

export type PeriodBlocks = Record<string, PeriodBlock>;

export interface Gap {
  id: string;
  blockKey: string;
  description: string;
  status: "open" | "filled";
  userResponse: string | null;
  createdAt?: string;
}

export interface OutstandingQuestion {
  id: string;
  blockKey: string | null;
  question: string;
  status: "pending" | "answered";
  userAnswer: string | null;
  createdAt?: string;
}

export interface RoadmapVersion {
  id: string;
  sessionId: string;
  version: number;
  label: string | null;
  buildingBlocks: AllBuildingBlocks;
  gaps: Gap[];
  outstandingQuestions: OutstandingQuestion[];
  createdAt: string;
}

export interface ResearchRun {
  triggeredAt: string;
  triggerSource: "auto" | "manual";
  additionalContext?: string;
  summary?: string;
  completedAt?: string | null;
}

export interface SessionData {
  id: string;
  companyName: string;
  domain?: string;
  context?: string;
  status: string;
  currentPhase: number;
  buildingBlocks: AllBuildingBlocks;
  gaps: Gap[];
  outstandingQuestions: OutstandingQuestion[];
  researchComplete: boolean;
  researchStartedAt?: string | null;
  researchRuns?: ResearchRun[];
  draftVersion: number;
  publishedAt?: string | null;
  publishedBlocks?: AllBuildingBlocks | null;
  shareToken?: string | null;
  sessionStartedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  peakDate?: string | null;       // ISO date "2030-12-31"
  yearStartMonth: number;         // 1–12, default 1
  yearStartDay: number;           // 1–28, default 1
  userRole?: "owner" | "advisor" | "view" | "comment" | "solo_contributor" | "team_member";
  collaboratorRole?: string;      // present on sessions shared with the current user
  linkedSessionId?: string | null;
  /** @deprecated Solo session clones are gone post-refactor. Field kept on the
   *  type only so old cached client state doesn't blow up. */
  linkType?: string | null;
  /** @deprecated see linkType */
  parentSessionName?: string | null;
  contributionCount?: number;         // owner only: # of contributors with ≥1 submitted block
  /** @deprecated init_blank lives on roadmap_outputs now */
  initBlank?: boolean;
  contributionDeadline?: string | null;  // collaborator deadline to submit
  submittedAt?: string | null;           // collaborator: when contribution was submitted
  advisors: SessionAdvisor[];
  progressSettings?: ProgressSettings;
  visibilityMap?: VisibilityMap;
  lockedBlocks?: Record<string, boolean>;
  lockedQuarters?: Record<string, { locked_at: string; locked_by: string }>;
  lockedAnnualPeriods?: Record<string, { locked_at: string; locked_by: string }>;
  lockedThreeYearPeriods?: Record<string, { locked_at: string; locked_by: string }>;
  lockedPeakPeriods?: Record<string, { locked_at: string; locked_by: string }>;
  categoryCatalog?: string[];          // Roadmap-level category label catalog
  /** @deprecated kept on the type for legacy responses; never use for navigation. */
  ownerSoloSessionId?: string | null;
  periodBlocks?: PeriodBlocks;
  /** Backref array of roadmap_outputs._id for this session. */
  outputs?: string[];
  /** The current viewer's roadmap_outputs doc (private workspace). */
  viewerOutput?: RoadmapOutput | null;
}

/** Per-user private workspace for a roadmap (mirrors backend `roadmap_outputs`). */
export interface RoadmapOutput {
  id: string;
  owner: string;
  roadmapSession: string;
  buildingBlocks: AllBuildingBlocks;
  periodBlocks?: PeriodBlocks;
  // Gaps/questions inherit the same shape as their session-level
  // counterparts — _ser_session emits the doc field verbatim and the
  // legacy migration copied Gap[]/OutstandingQuestion[] objects from
  // solo sessions into roadmap_outputs.
  gaps: Gap[];
  outstandingQuestions: OutstandingQuestion[];
  currentPhase: number;
  initBlank: boolean;
  categoryCatalog?: string[] | null;
  versions?: Record<string, unknown>;
}

export interface SessionAdvisor {
  userId: string;
  name: string;
  email: string;
  invitedAt: string;
}

export type CollaboratorAccessLevel = "view" | "comment" | "solo_contributor" | "team_member" | "owner";
export type CollaboratorStatus = "pending" | "accepted" | "declined";

export interface Collaborator {
  id: string;
  sessionId: string;
  invitedBy: string;
  inviteeEmail: string;
  inviteeUserId?: string | null;
  inviteeName?: string | null;
  accessLevel: CollaboratorAccessLevel;
  status: CollaboratorStatus;
  soloSessionId?: string | null;
  initBlank?: boolean;
  contributionDeadline?: string | null;
  createdAt: string;
  acceptedAt?: string | null;
}

export interface ChatMessage {
  id: number | string;
  sessionId: string;
  role: "user" | "assistant";
  content: string;
  messageType?: string;
  createdAt: string;
}

export interface StreamChunk {
  content?: string;
  buildingBlocks?: AllBuildingBlocks;
  gaps?: Gap[];
  outstandingQuestions?: OutstandingQuestion[];
  phase?: number;
  researchComplete?: boolean;
  done?: boolean;
}

// Used by AssumptionsPanel
export interface Assumption {
  blockKey: string;
  blockLabel: string;
  itemIndex: number;
  item: WMBT;
}

export interface Attachment {
  id: string;
  filename: string;
  sourceType: string;
  contentLength?: number;
  createdAt?: string;
}

// Phase 4 — Pending Contributions
export interface PendingContribution {
  collabId: string;
  soloSessionId: string;
  name: string;
  email: string;
  hasSubmitted: boolean;
  submittedAt: string | null;
  blocks: Record<string, unknown>;
}

// Phase 4 — Alignment Synthesis
export interface AlignmentReport {
  id: string;
  sessionId: string;
  createdAt: string;
  contributors: Array<{ userId: string; name: string; soloSessionId: string }>;
  reportMarkdown: string;
  proposedBlocks: AllBuildingBlocks;
  appliedAt?: string | null;
}

// Phase 2 — Progress & Reflection
export interface ProgressPeriodState {
  pctComplete: number;
  rationale?: string;
  whatsWorking?: string;
  challenges?: string;
  focusAhead?: string;
  lastUpdatedBy?: string;
  lastUpdatedByName?: string;
  lastUpdatedAt?: string;
}

export interface ProgressPeriod {
  id: string;
  sessionId: string;
  periodType: "weekly" | "qbr" | "annual";
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  blockKey: string;
  currentState: Record<string, ProgressPeriodState>;
  history: ProgressPeriodState[];
  isFinalized: boolean;
  finalizedAt?: string | null;
  owner: string;
  createdAt: string;
  updatedAt: string;
}

// Contribution visibility settings (stored on session doc)
export interface ProgressSettings {
  weekly_visibility: "open" | "gated";
  qbr_visibility: "open" | "gated";
}

// Per-period visibility map — replaces ProgressSettings for new per-period system
export type VisibilityMap = Record<string, "open" | "gated">;

export function isVisibleForPeriod(
  map: VisibilityMap | undefined | null,
  periodType: string,
  periodKey: string,
  opts?: { activeWeeklyKey?: string | null; lockedQuarterKeys?: string[] | null }
): boolean {
  const key = `${periodType}:${periodKey}`;
  const value = map?.[key];
  if (value !== undefined) return value === "open";
  if (periodType === "weekly" && opts?.activeWeeklyKey) {
    return periodKey === opts.activeWeeklyKey;
  }
  if (periodType === "nqo" && opts?.lockedQuarterKeys) {
    return opts.lockedQuarterKeys.includes(periodKey);
  }
  return false;
}

// ── PAP Plan types ────────────────────────────────────────────────────────────

export type PAPPhase = "pre" | "during" | "post";
export type PAPItemType = "round" | "platform_action" | "communication" | "meeting_agenda" | "internal";
export type PAPAutomation = "auto" | "manual";
export type PAPItemStatus = "pending" | "in_progress" | "done" | "skipped";

export interface PAPTemplateItem {
  id: string;
  phase: PAPPhase;
  title: string;
  description: string;
  type: PAPItemType;
  assigned_role: "advisor" | "owner" | "team";
  offset_days: number;
  automation: PAPAutomation;
  automation_config: Record<string, unknown>;
  notify_advisor_on_completion: boolean;
  sort_order: number;
}

export interface PAPTemplate {
  id: string;
  session_id: string;
  period_type: string;
  items: PAPTemplateItem[];
  created_at: string;
  updated_at: string;
}

export interface PAPInstanceItemState {
  template_item_id: string;
  scheduled_date: string;
  status: PAPItemStatus;
  automation_override: PAPAutomation | null;
  fired_at: string | null;
  linked_artifact: { type: string; ref_id: string } | null;
  notes: string;
  completed_at: string | null;
}

export interface PAPInstance {
  id: string;
  session_id: string;
  period_type: string;
  period_key: string;
  anchor_date: string;
  status: "active" | "completed" | "archived";
  item_states: PAPInstanceItemState[];
  automation_paused: boolean;
  created_at: string;
  updated_at: string;
}
